/**
 * Raw-fetch client for oxbo-auth `/v1/admin/*` endpoints not covered by
 * @oxbo/auth-client (added in AUTH-B6/B7; SDK v0.1.0 predates them).
 *
 * ⚠️ DUAL-GATE: every `/v1/admin/*` endpoint requires `super_admin` AND the
 * `X-OXBO-Admin-Mode: true` header. Without the header a super_admin gets a 403
 * (not silent partial data) — so we send it on EVERY request here.
 *
 * Auth: Bearer token (password flow) when present, plus `credentials: "include"`
 * so the SSO cookie flow authenticates too.
 *
 * Contract verified against https://auth.dev.oxbo.energy/openapi.json (v0.5.0).
 */
import { env } from "../config/env";
import { tokenStore } from "./tokenStore";

export type SystemRole = "super_admin" | "industry_trustee";
export type UserStatus = "active" | "suspended";
export type OrgRole = "owner" | "admin" | "member";

// --- Users (B7) ------------------------------------------------------------

export interface AdminUserSummary {
  id: string;
  email: string;
  status: string;
  full_name: string | null;
  org_roles: string[];
  system_roles: string[];
  created_at: string;
}

export interface AdminUserDetail extends AdminUserSummary {
  email_verified_at: string | null;
}

export interface UserListResponse {
  users: AdminUserSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface SetStatusResponse {
  user_id: string;
  status: string;
  previous_status: string;
}

export interface ListUsersParams {
  q?: string;
  org_id?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

export class AdminApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(`AdminApiError ${status}`);
    this.name = "AdminApiError";
  }
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    // Dual-gate: required on all /v1/admin/* calls (super_admin + this header).
    "X-OXBO-Admin-Mode": "true",
  };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const token = tokenStore.getAccessToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${env.authApiUrl}${path}`, {
    method,
    headers,
    credentials: "include",
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (res.status === 204) return undefined as unknown as T;

  const ct = res.headers.get("Content-Type") ?? "";
  const parsed = ct.includes("application/json") ? await res.json() : await res.text();
  if (!res.ok) throw new AdminApiError(res.status, parsed);
  return parsed as T;
}

// --- System roles (B6) -----------------------------------------------------

/** POST /v1/admin/users/{user_id}/system-roles — grant a global system role. */
export function grantSystemRole(userId: string, role: SystemRole): Promise<unknown> {
  return request("POST", `/v1/admin/users/${encodeURIComponent(userId)}/system-roles`, {
    role,
  });
}

/** DELETE /v1/admin/users/{user_id}/system-roles/{role} — revoke a system role. */
export function revokeSystemRole(userId: string, role: SystemRole): Promise<void> {
  return request<void>(
    "DELETE",
    `/v1/admin/users/${encodeURIComponent(userId)}/system-roles/${encodeURIComponent(role)}`,
  );
}

// --- Users (B7) ------------------------------------------------------------

/** GET /v1/admin/users?q=&org_id=&status=&limit=&offset= */
export function listUsers(params: ListUsersParams = {}): Promise<UserListResponse> {
  return request<UserListResponse>("GET", `/v1/admin/users${buildQuery({ ...params })}`);
}

/** GET /v1/admin/users/{user_id} */
export function getUser(userId: string): Promise<AdminUserDetail> {
  return request<AdminUserDetail>("GET", `/v1/admin/users/${encodeURIComponent(userId)}`);
}

/** POST /v1/admin/users/{user_id}/status — {status: active|suspended} (audited). */
export function setUserStatus(userId: string, status: UserStatus): Promise<SetStatusResponse> {
  return request<SetStatusResponse>(
    "POST",
    `/v1/admin/users/${encodeURIComponent(userId)}/status`,
    { status },
  );
}

// --- Organizations + org-roles (B7) ---------------------------------------
//
// NB: the organizations view is DERIVED (there is no real org registry in
// oxbo-auth). `name` and `created_at` are best-effort / non-authoritative — the
// UI labels them as such.

export interface OrgMember {
  user_id: string;
  email: string;
  org_roles: string[];
}

export interface OrganizationSummary {
  id: string;
  name: string | null;
  member_count: number;
  created_at: string;
}

export interface OrganizationDetail extends OrganizationSummary {
  members: OrgMember[];
}

export interface OrganizationListResponse {
  organizations: OrganizationSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface RoleMutationResponse {
  organization_id: string;
  user_id: string;
  role: string;
  already_assigned: boolean;
}

export interface ListOrganizationsParams {
  limit?: number;
  offset?: number;
}

/** GET /v1/admin/organizations?limit=&offset= */
export function listOrganizations(
  params: ListOrganizationsParams = {},
): Promise<OrganizationListResponse> {
  return request<OrganizationListResponse>(
    "GET",
    `/v1/admin/organizations${buildQuery({ ...params })}`,
  );
}

/** GET /v1/admin/organizations/{org_id} — detail incl. members. */
export function getOrganization(orgId: string): Promise<OrganizationDetail> {
  return request<OrganizationDetail>(
    "GET",
    `/v1/admin/organizations/${encodeURIComponent(orgId)}`,
  );
}

/** PUT /v1/admin/organizations/{org_id}/members/{user_id}/roles/{role} (audited). */
export function assignOrgRole(
  orgId: string,
  userId: string,
  role: OrgRole,
): Promise<RoleMutationResponse> {
  return request<RoleMutationResponse>(
    "PUT",
    `/v1/admin/organizations/${encodeURIComponent(orgId)}/members/${encodeURIComponent(
      userId,
    )}/roles/${encodeURIComponent(role)}`,
  );
}

/** DELETE /v1/admin/organizations/{org_id}/members/{user_id}/roles/{role} (audited). */
export function revokeOrgRole(orgId: string, userId: string, role: OrgRole): Promise<void> {
  return request<void>(
    "DELETE",
    `/v1/admin/organizations/${encodeURIComponent(orgId)}/members/${encodeURIComponent(
      userId,
    )}/roles/${encodeURIComponent(role)}`,
  );
}
